// src/components/Basket/QuantitySelector.jsx
import React, { useState, useEffect, useRef } from 'react';
import { toPersianNumbers } from "../../utils/toPersianNumbers";

const QuantitySelector = ({
  value,
  onChange,
  onRemove, // تابع حذف آیتم
  showRemoveButton = false, // آیا دکمه حذف نمایش داده بشه؟
  min = 1,
  max,
  multiple = 1,
  loading = false,
  disabled = false,
  className = "",
  showInventory = true,
  inventoryCount = 0,
  itemId // برای شناسایی آیتم در زمان حذف (اختیاری)
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleIncrease = () => {
    if (loading || disabled) return;
    const newValue = localValue + multiple;
    if (max && newValue>max) {
      setLocalValue(max);
      onChange(max);
    } else {
      setLocalValue(newValue);
      onChange(newValue);}
  };

  const handleDecrease = () => {
    if (loading || disabled) return;
    const newValue = localValue - multiple;
    if (newValue < min) {
      setLocalValue(min);
      onChange(min);
    } else {
    setLocalValue(newValue);
    onChange(newValue);}
  };

  const handleInputChange = (e) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val) && val >= 0) {
      setLocalValue(val);
    } else if (e.target.value === '') {
      setLocalValue('');
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);

    // اعتبارسنجی نهایی
    let finalValue = localValue;
    if (localValue === '' || isNaN(parseInt(localValue))) {
      finalValue = min;
    } else if (max && localValue > max) {
      finalValue = max;
    } else if (localValue < min) {
      finalValue = min;
    }

    setLocalValue(finalValue);
    if (finalValue !== value) {
      onChange(finalValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      setLocalValue(value);
      setIsEditing(false);
    }
  };

  const handleRemove = async () => {
    if (!onRemove || removeLoading) return;

    setRemoveLoading(true);
    try {
      await onRemove(itemId);
    } catch (error) {
      console.error("Error removing item:", error);
    } finally {
      setRemoveLoading(false);
    }
  };

  const isMaxReached = max && localValue >= max;
  const isMinReached = localValue <= min;

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        {/* Selector اصلی */}
        <div
          className={`flex items-center bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden ${
            disabled || loading ? 'opacity-50' : ''
          } ${className}`}
        >
          {/* دکمه افزایش */}
          <button
            onClick={handleIncrease}
            disabled={disabled || loading || isMaxReached}
            className="w-9 h-9 flex items-center justify-center text-gray-700 hover:bg-gray-100 transition disabled:opacity-30 disabled:cursor-not-allowed active:bg-gray-200"
            aria-label="افزایش تعداد"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          {/* نمایش/ورودی تعداد - قابل کلیک برای ویرایش */}
          {isEditing ? (
            <input
              ref={inputRef}
              type="number"
              value={localValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              min={min}
              max={max}
              disabled={loading || disabled}
              className="w-16 text-center text-sm font-medium text-gray-800 border-0 focus:ring-1 focus:ring-blue-500 rounded"
            />
          ) : (
            <div
              onClick={() => !loading && !disabled && setIsEditing(true)}
              className="w-16 text-center text-sm font-medium text-gray-800 cursor-pointer hover:bg-gray-50 py-2 transition"
            >
              {toPersianNumbers(localValue)}
            </div>
          )}

          {/* دکمه کاهش */}
          <button
            onClick={handleDecrease}
            disabled={disabled || loading || isMinReached}
            className="w-9 h-9 flex items-center justify-center text-gray-700 hover:bg-gray-100 transition disabled:opacity-30 disabled:cursor-not-allowed active:bg-gray-200"
            aria-label="کاهش تعداد"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
        </div>

        {/* دکمه حذف (اختیاری) */}
        {showRemoveButton && (
          <button
            onClick={handleRemove}
            disabled={disabled || loading || removeLoading}
            className="w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition disabled:opacity-50"
            title="حذف از سبد خرید"
          >
            {removeLoading ? (
              <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            )}
          </button>
        )}
      </div>

      {/* نمایش موجودی */}
      {showInventory && inventoryCount > 0 && (
        <div className="absolute -bottom-5 left-0 text-xs text-gray-500">
          {toPersianNumbers(inventoryCount)} عدد موجود
        </div>
      )}
    </div>
  );
};

export default QuantitySelector;